/**
 * Color Picker Overlay
 * Shows color preview while using color picker tool and notification when picked
 */

import React from 'react';
import { Pipette, Check, Copy } from 'lucide-react';

interface ColorPickerOverlayProps {
  isActive: boolean;
  previewColor: string | null;
  pickedColor: string | null;
  showPickedNotification: boolean;
}

export function ColorPickerOverlay({
  isActive,
  previewColor,
  pickedColor,
  showPickedNotification,
}: ColorPickerOverlayProps) {
  // Show nothing if color picker isn't active and no notification
  if (!isActive && !showPickedNotification) return null;

  return (
    <>
      {/* Bottom bar showing current preview color */}
      {isActive && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="bg-navy-light/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl px-4 py-3 flex items-center gap-3">
            <Pipette size={18} className="text-primary" />

            {previewColor ? (
              <>
                {/* Color swatch */}
                <div
                  className="w-8 h-8 rounded-lg border border-white/20 shadow-inner"
                  style={{ backgroundColor: previewColor }}
                />

                {/* Color value */}
                <div className="flex flex-col">
                  <span className="text-xs text-fg-subtle">Hover color</span>
                  <span className="text-sm font-mono text-fg font-medium">
                    {previewColor.toUpperCase()}
                  </span>
                </div>
              </>
            ) : (
              <span className="text-sm text-fg-muted">
                Hover over a layer to preview its color
              </span>
            )}
          </div>
        </div>
      )}

      {/* Picked notification */}
      {showPickedNotification && pickedColor && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="bg-green-900/90 backdrop-blur-xl border border-green-500/30 rounded-xl shadow-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Check size={18} className="text-green-400" />
            </div>

            {/* Color swatch */}
            <div
              className="w-8 h-8 rounded-lg border border-white/20 shadow-inner"
              style={{ backgroundColor: pickedColor }}
            />

            {/* Color value */}
            <div className="flex flex-col">
              <span className="text-xs text-green-300">Copied to clipboard</span>
              <span className="text-sm font-mono text-white font-medium">
                {pickedColor.toUpperCase()}
              </span>
            </div>

            <Copy size={14} className="text-green-400 ml-1" />
          </div>
        </div>
      )}
    </>
  );
}
